import { useState, useMemo, useRef, useEffect } from "react";
import vocabData from "../ScienceVocabPractice/ScienceVocabPool.js";
import { awardToken } from "../../../awardtoken.js";
import "./ScienceJeopardy.css";
import { db } from "../../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../../components/useAuth";

const TOOL_TYPE = "ScienceJeopardyPractice";
const POINTS = [100, 200, 300, 400, 500];

async function logJeopardyPractice({ user, grade, score, correctCount, answeredCount, totalQuestions, startTime, endTime }) {
  if (!user?.uid) return;
  await setDoc(
    doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
    {
      toolType: TOOL_TYPE,
      topic: `Science Jeopardy (Grade ${grade})`,
      correct: correctCount,
      total: answeredCount,
      accuracy: answeredCount > 0 ? Math.round((correctCount/answeredCount)*100) : null,
      score,
      answeredCount,
      totalQuestions,
      timestamp: serverTimestamp(),
      startedAt: startTime,
      finishedAt: endTime,
      durationSec: Math.round((endTime - startTime) / 1000),
    },
    { merge: true }
  );
}


function generateJeopardyQuestions(grade = 7) {
  const isJunior = grade <= 7;
  const CATEGORIES = isJunior
    ? [{ key: "general", label: "General Science" }]
    : [
        { key: "biology", label: "Biology" },
        { key: "physics", label: "Physics" },
        { key: "chemistry", label: "Chemistry" },
      ];

  let pools;
  if (isJunior) {
    pools = { general: vocabData[grade] || [] };
  } else {
    pools = {
      biology: vocabData[grade]?.biology || [],
      physics: vocabData[grade]?.physics || [],
      chemistry: vocabData[grade]?.chemistry || [],
    };
  }

  const board = CATEGORIES.map(cat => ({
    category: cat.label,
    questions: POINTS.map((pt, i) => {
      let pool = pools[cat.key] || [];
      const idx = i % (pool.length || 1);
      const q = pool.length ? pool[idx] : { term: "N/A", definition: "No data." };
      return {
        points: pt,
        question: q.definition || "No question.",
        answer: q.term || "",
        answered: false
      };
    })
  }));

  return { board, CATEGORIES };
}

export default function ScienceJeopardy() {
  const [grade, setGrade] = useState(8);
  const { board, CATEGORIES } = useMemo(() => generateJeopardyQuestions(grade), [grade]);
  const [boardState, setBoardState] = useState(board);
  const [activeQ, setActiveQ] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [score, setScore] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const tokensAwarded = useRef(0);
  const totalQuestions = CATEGORIES.length * POINTS.length;
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const [correctCount, setCorrectCount] = useState(0);

  // Log after every answer
  useEffect(() => {
    if (answeredCount > 0) {
      const endTime = Date.now();
      logJeopardyPractice({
        user,
        grade,
        score,
        correctCount,
        answeredCount,
        totalQuestions,
        startTime: sessionStart,
        endTime,
      });
    }
    // eslint-disable-next-line
  }, [answeredCount, score]);

  // Log on unmount
  useEffect(() => {
    return () => {
      if (answeredCount > 0) {
        logJeopardyPractice({
          user,
          grade,
          score,
          answeredCount,
          totalQuestions,
          startTime: sessionStart,
          endTime: Date.now(),
        });
      }
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // 每 3000 分給 1 枚 token
    if (score >= (tokensAwarded.current + 1) * 3000) {
      awardToken();
      tokensAwarded.current += 1;
    }
  }, [score]);

  // 選格子
  function handlePick(catIdx, qIdx) {
    if (boardState[catIdx].questions[qIdx].answered) return;
    setActiveQ({ catIdx, qIdx, ...boardState[catIdx].questions[qIdx] });
    setUserAnswer("");
  }

  // 答題
function handleSubmit(e) {
  e.preventDefault();
  const isCorrect = userAnswer.trim().toLowerCase() === activeQ.answer.trim().toLowerCase();
  let updatedBoard = boardState.map((cat, cidx) =>
    cidx === activeQ.catIdx
      ? {
          ...cat,
          questions: cat.questions.map((q, qidx) =>
            qidx === activeQ.qIdx
              ? { ...q, answered: true, correct: isCorrect }
              : q
          )
        }
      : cat
  );
  setBoardState(updatedBoard);
  setScore(s => s + (isCorrect ? activeQ.points : 0));
  setAnsweredCount(c => c + 1);

  // *** ADD THIS: increment correctCount if answer is correct
  if (isCorrect) setCorrectCount(c => c + 1);

  setActiveQ({
    ...activeQ,
    showResult: true,
    userAnswer,
    isCorrect
  });
}

  // 下一題或關閉
  function handleClose() {
    setActiveQ(null);
  }

  // 重玩
  function handleReset() {
    const { board: newBoard } = generateJeopardyQuestions(grade);
    setBoardState(newBoard);
    setScore(0);
    setAnsweredCount(0);
    setActiveQ(null);
    tokensAwarded.current = 0;
    setSessionStart(Date.now());
    // Reset dashboard immediately
    logJeopardyPractice({
      user,
      grade,
      score: 0,
      answeredCount: 0,
      totalQuestions,
      startTime: Date.now(),
      endTime: Date.now(),
    });
  }

  // Also reset on grade change
  useEffect(() => {
    handleReset();
    // eslint-disable-next-line
  }, [grade]);

  return (
    <div className="jeopardy-container">
      <h2>Science Jeopardy!</h2>
      <label style={{ marginBottom: 16 }}>
        <b>Grade:</b>{" "}
        <select value={grade} onChange={e => setGrade(Number(e.target.value))}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>
              Grade {i + 1}
            </option>
          ))}
        </select>
      </label>
      <div className="jeopardy-score">Score: {score}</div>
      <div className="jeopardy-board">
        <div className="jeopardy-row jeopardy-header">
          {CATEGORIES.map(cat => (
            <div className="jeopardy-cell jeopardy-cat" key={cat.key}>
              {cat.label}
            </div>
          ))}
        </div>
        {POINTS.map((pt, rowIdx) => (
          <div className="jeopardy-row" key={pt}>
            {CATEGORIES.map((cat, colIdx) => {
              const qObj = boardState[colIdx].questions[rowIdx];
              return (
                <button
                  key={colIdx}
                  className={"jeopardy-cell jeopardy-q" + (qObj.answered ? " jeopardy-answered" : "")}
                  disabled={qObj.answered}
                  onClick={() => handlePick(colIdx, rowIdx)}
                >
                  {qObj.answered ? "—" : pt}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {/* 顯示題目對話框 */}
      {activeQ && (
        <div className="jeopardy-modal">
          <div className="jeopardy-modal-content">
            <h3>
              {CATEGORIES[activeQ.catIdx].label} - {activeQ.points} pts
            </h3>
            <div style={{ margin: "1.2em 0" }}>
              <b>Definition:</b> {activeQ.question}
            </div>
            {activeQ.showResult ? (
              <div>
                <div style={{ margin: "1em 0" }}>
                  {activeQ.isCorrect ? (
                    <span style={{ color: "#26d991", fontWeight: 600 }}>
                      Correct! +{activeQ.points}
                    </span>
                  ) : (
                    <span style={{ color: "#fd5578", fontWeight: 600 }}>
                      Incorrect. The answer was: {activeQ.answer}
                    </span>
                  )}
                </div>
                <button onClick={handleClose}>Continue</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <input
                  type="text"
                  value={userAnswer}
                  onChange={e => setUserAnswer(e.target.value)}
                  autoFocus
                  placeholder="Your answer..."
                />
                <button type="submit" style={{ marginLeft: 12 }}>
                  Submit
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      {answeredCount === totalQuestions && (
        <div style={{ margin: "2em 0", fontWeight: 700, fontSize: "1.3em" }}>
          🎉 Game over! Final Score: {score} <br />
          <button style={{ marginTop: 18 }} onClick={handleReset}>
            Play Again
          </button>
        </div>
      )}
    </div>
  );
}
